import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { hasTier } from "@/lib/tiers";
import { PaywallGate } from "@/components/PaywallGate";
import { Compass, CalendarClock, Video, Loader2, CheckCircle2, XCircle, Plus, Trash2, ExternalLink, ShieldCheck, AlertTriangle } from "lucide-react";

const COPY = {
  en: {
    kicker: "DIY Legal · Court Ready +",
    heading: "Community Court Navigator sessions",
    sub: "Book a 30-minute virtual session for court-navigation, procedural education, or help using DIY Legal. Not legal advice.",
    quota: (used, limit) => `${used} of ${limit} sessions used this year`,
    available: "Available times",
    empty_available: "No open times right now. Check back soon — new times are added weekly.",
    topic_label: "What do you want help with?",
    topic_placeholder: "e.g., I need help understanding my court date checklist and preparing for the hearing.",
    book: "Book this time",
    booking: "Booking...",
    booked_title: "Session confirmed",
    booked_body: "You'll get a confirmation by email. Join the video call from the link below at your scheduled time.",
    join: "Join the video call",
    my: "Your sessions",
    empty_my: "You have no sessions yet.",
    cancel: "Cancel",
    canceling: "Canceling...",
    canceled: "Canceled",
    admin: "Admin · Create availability",
    admin_note: "Publish 30-min slots users can book. Times are stored in UTC.",
    admin_from: "Starts (local)",
    admin_to: "Ends (local)",
    admin_note_label: "Note (optional)",
    admin_add: "Publish slot",
    admin_seed: "Publish next 4 weeks of intro slots",
    admin_seed_done: (n) => `Added ${n} slot${n === 1 ? "" : "s"}`,
    admin_manage: "All published slots",
    admin_manage_empty: "No upcoming slots published.",
    admin_locked: "Booked — cannot unpublish",
    admin_unpublish: "Unpublish",
    admin_confirm_unpublish: "Unpublish this slot?",
    cal_heading: "Google Calendar (per-session Meet)",
    cal_desc: "Connect your Google account so every booking creates its own Meet room on your calendar (and emails the guest a calendar invite).",
    cal_connect: "Connect Google Calendar",
    cal_reconnect: "Reconnect",
    cal_disconnect: "Disconnect",
    cal_connected: (email) => `Connected as ${email}`,
    cal_disconnected: "Not connected — bookings will use the shared Meet link.",
    cal_error: "Calendar connection error — please reconnect.",
    cal_not_configured: "Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to backend/.env then restart the backend to enable this.",
    cal_toast_connected: "Google Calendar connected — per-session Meet rooms are on",
    cal_toast_disconnected: "Google Calendar disconnected — bookings will use the shared Meet link",
    cal_toast_error: "Calendar connection failed. Please try again.",
    error_prefix: "Something went wrong:",
  },
  es: {
    kicker: "DIY Legal · Court Ready +",
    heading: "Sesiones con la Navegadora Comunitaria",
    sub: "Reserva una sesión virtual de 30 minutos para navegación de corte, educación procesal o ayuda usando DIY Legal. No es asesoría legal.",
    quota: (used, limit) => `${used} de ${limit} sesiones usadas este año`,
    available: "Horarios disponibles",
    empty_available: "No hay horarios abiertos ahora. Vuelve pronto — se agregan nuevos horarios cada semana.",
    topic_label: "¿Con qué quieres ayuda?",
    topic_placeholder: "ej., Necesito ayuda entendiendo mi lista de preparación y la audiencia.",
    book: "Reservar este horario",
    booking: "Reservando...",
    booked_title: "Sesión confirmada",
    booked_body: "Recibirás una confirmación por correo. Únete a la videollamada desde el enlace de abajo a la hora programada.",
    join: "Unirse a la videollamada",
    my: "Tus sesiones",
    empty_my: "Aún no tienes sesiones.",
    cancel: "Cancelar",
    canceling: "Cancelando...",
    canceled: "Cancelada",
    admin: "Admin · Crear disponibilidad",
    admin_note: "Publica horarios de 30 min que los usuarios puedan reservar. Los tiempos se guardan en UTC.",
    admin_from: "Inicio (local)",
    admin_to: "Fin (local)",
    admin_note_label: "Nota (opcional)",
    admin_add: "Publicar horario",
    admin_seed: "Publicar 4 semanas de horarios intro",
    admin_seed_done: (n) => `Se agregaron ${n} horario${n === 1 ? "" : "s"}`,
    admin_manage: "Todos los horarios publicados",
    admin_manage_empty: "No hay horarios próximos publicados.",
    admin_locked: "Reservado — no se puede quitar",
    admin_unpublish: "Quitar",
    admin_confirm_unpublish: "¿Quitar este horario?",
    cal_heading: "Google Calendar (Meet por sesión)",
    cal_desc: "Conecta tu cuenta de Google para que cada reserva cree su propia sala de Meet en tu calendario (y envíe una invitación al invitado).",
    cal_connect: "Conectar Google Calendar",
    cal_reconnect: "Reconectar",
    cal_disconnect: "Desconectar",
    cal_connected: (email) => `Conectado como ${email}`,
    cal_disconnected: "No conectado — las reservas usarán el enlace de Meet compartido.",
    cal_error: "Error de conexión — vuelve a conectar.",
    cal_not_configured: "Agrega GOOGLE_CLIENT_ID y GOOGLE_CLIENT_SECRET a backend/.env y reinicia el backend para habilitar esto.",
    cal_toast_connected: "Google Calendar conectado — las salas de Meet por sesión están activas",
    cal_toast_disconnected: "Google Calendar desconectado — las reservas usarán el enlace compartido",
    cal_toast_error: "La conexión falló. Intenta de nuevo.",
    error_prefix: "Algo salió mal:",
  },
};

function fmt(when, lang) {
  try {
    return new Date(when).toLocaleString(lang === "es" ? "es-US" : "en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch { return when; }
}

function SessionsApp() {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [slots, setSlots] = useState([]);
  const [mine, setMine] = useState([]);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [booking, setBooking] = useState(false);
  const [err, setErr] = useState("");
  const [justBooked, setJustBooked] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  // admin
  const [aFrom, setAFrom] = useState("");
  const [aTo, setATo] = useState("");
  const [aNote, setANote] = useState("");
  const [aBusy, setABusy] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [unpubId, setUnpubId] = useState(null);
  const [calStatus, setCalStatus] = useState(null); // {configured, connected, email, error}
  const [calBusy, setCalBusy] = useState(false);
  const [sp, setSp] = useSearchParams();

  const load = async () => {
    try {
      const [s, m] = await Promise.all([api.get("/sessions/slots"), api.get("/sessions/my")]);
      setSlots(s.data.slots || []);
      setMine(m.data.bookings || []);
    } catch {}
  };

  const loadAllSlots = async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/sessions/slots/all");
      setAllSlots(data.slots || []);
    } catch {}
  };

  const loadCalStatus = async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/calendar/status");
      setCalStatus(data);
    } catch {
      setCalStatus(null);
    }
  };

  const connectCalendar = async () => {
    if (calBusy) return;
    setCalBusy(true);
    try {
      const { data } = await api.get("/calendar/oauth/start");
      if (data?.authorization_url) window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || c.cal_toast_error);
    } finally {
      setCalBusy(false);
    }
  };

  const disconnectCalendar = async () => {
    if (calBusy) return;
    setCalBusy(true);
    try {
      await api.post("/calendar/disconnect");
      toast(c.cal_toast_disconnected);
      await loadCalStatus();
    } finally {
      setCalBusy(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadAllSlots(); loadCalStatus(); }, [user?.role]);

  // React to Google OAuth callback bounce-back
  useEffect(() => {
    if (user?.role !== "admin") return;
    const status = sp.get("calendar");
    if (!status) return;
    if (status === "connected") {
      toast.success(c.cal_toast_connected);
      loadCalStatus();
    } else if (status === "error" || status === "exchange_failed" || status === "state_mismatch") {
      toast.error(c.cal_toast_error);
    }
    // Clear the query param so a refresh doesn't re-toast
    sp.delete("calendar");
    setSp(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, user?.role]);

  const usage = (user?.usage?.navigator_sessions) || 0;
  const limit = user?.limits?.navigator_sessions || 12;

  const doBook = async () => {
    if (!pickedSlot || !topic.trim() || booking) return;
    setBooking(true);
    setErr("");
    try {
      const { data } = await api.post("/sessions/book", { slot_id: pickedSlot.id, topic: topic.trim() });
      setJustBooked(data);
      setPickedSlot(null); setTopic("");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const doCancel = async (id) => {
    setCancelId(id);
    try {
      await api.post(`/sessions/${id}/cancel`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setCancelId(null);
    }
  };

  const doPublish = async () => {
    if (!aFrom || !aTo || aBusy) return;
    setABusy(true);
    setErr("");
    try {
      await api.post("/sessions/slots", {
        starts_at: new Date(aFrom).toISOString(),
        ends_at: new Date(aTo).toISOString(),
        note: aNote.trim() || null,
      });
      setAFrom(""); setATo(""); setANote("");
      await load();
      await loadAllSlots();
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setABusy(false);
    }
  };

  const doSeed = async () => {
    if (aBusy) return;
    setABusy(true);
    setErr("");
    try {
      const { data } = await api.post("/sessions/slots/seed", {});
      await load();
      await loadAllSlots();
      // sonner is available globally via Layout; use dynamic import to avoid hard dep
      try {
        const { toast } = await import("sonner");
        toast.success(c.admin_seed_done(data.inserted || 0));
      } catch {}
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setABusy(false);
    }
  };

  const doUnpublish = async (slotId) => {
    if (unpubId) return;
    if (!window.confirm(c.admin_confirm_unpublish)) return;
    setUnpubId(slotId);
    setErr("");
    try {
      await api.delete(`/sessions/slots/${slotId}`);
      await loadAllSlots();
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setUnpubId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-1" data-testid="sessions-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32] font-extrabold inline-flex items-center gap-2">
          <Compass className="w-3.5 h-3.5" /> {c.kicker}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-[#1C2024]">{c.heading}</h1>
        <p className="mt-2 text-[#4A525A] max-w-2xl mx-auto">{c.sub}</p>
        <div className="mt-3 inline-flex items-center gap-2 bg-[#FAF5EC] border-2 border-[#E4DDD3] text-[#1C2024] text-xs font-extrabold uppercase tracking-widest px-3 py-1 rounded-full" data-testid="sessions-quota">
          <ShieldCheck className="w-3.5 h-3.5 text-[#C85A32]" /> {c.quota(usage, limit)}
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-red-800 bg-red-50 border-2 border-red-200 rounded-xl p-3" data-testid="sessions-error">{c.error_prefix} {err}</div>}

      {justBooked && (
        <div className="mt-6 rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-5" data-testid="sessions-confirmed">
          <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-lg">
            <CheckCircle2 className="w-5 h-5" /> {c.booked_title}
          </div>
          <p className="mt-1 text-emerald-900 text-sm">{c.booked_body}</p>
          <div className="mt-3 text-sm text-emerald-900"><strong>{fmt(justBooked.starts_at, lang)}</strong></div>
          {justBooked.meet_url && (
            <a href={justBooked.meet_url} target="_blank" rel="noopener noreferrer" data-testid="sessions-confirmed-join"
              className="pod-focus mt-4 inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-2.5 px-4 rounded-xl text-sm">
              <Video className="w-4 h-4" /> {c.join} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      <section className="mt-8">
        <h2 className="text-xl font-extrabold text-[#1C2024] flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#C85A32]" /> {c.available}</h2>
        {slots.length === 0 ? (
          <div className="mt-3 rounded-2xl bg-[#FAF5EC] border-2 border-[#E4DDD3] p-5 text-[#4A525A]" data-testid="sessions-empty">{c.empty_available}</div>
        ) : (
          <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {slots.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setPickedSlot(s)}
                  data-testid={`slot-${s.id}`}
                  className={`pod-focus w-full text-left rounded-xl border-2 p-4 transition-colors ${
                    pickedSlot?.id === s.id ? "border-[#C85A32] bg-[#FAF5EC]" : "border-[#E4DDD3] bg-white hover:border-[#C85A32]"
                  }`}
                >
                  <div className="font-bold text-[#1C2024]">{fmt(s.starts_at, lang)}</div>
                  {s.note && <div className="text-xs text-[#707880] mt-1">{s.note}</div>}
                </button>
              </li>
            ))}
          </ul>
        )}

        {pickedSlot && (
          <div className="mt-4 rounded-2xl bg-white border-2 border-[#C85A32] p-5 space-y-3" data-testid="sessions-book-form">
            <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.topic_label}</label>
            <textarea
              data-testid="sessions-topic"
              className="w-full min-h-[90px] rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-4 py-3 text-[#1C2024]"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={c.topic_placeholder}
              maxLength={600}
              required
            />
            <button
              onClick={doBook}
              disabled={booking || !topic.trim()}
              data-testid="sessions-book"
              className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-2.5 px-5 rounded-xl inline-flex items-center gap-2"
            >
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
              {booking ? c.booking : c.book}
            </button>
          </div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-extrabold text-[#1C2024]">{c.my}</h2>
        {mine.length === 0 ? (
          <div className="mt-3 rounded-2xl bg-[#FAF5EC] border-2 border-[#E4DDD3] p-5 text-[#4A525A]" data-testid="sessions-mine-empty">{c.empty_my}</div>
        ) : (
          <ul className="mt-3 space-y-2">
            {mine.map((b) => (
              <li key={b.id} data-testid={`booking-${b.id}`} className="rounded-xl bg-white border-2 border-[#E4DDD3] p-4 flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-bold text-[#1C2024]">{fmt(b.starts_at, lang)}</div>
                  <div className="text-sm text-[#4A525A] mt-0.5">{b.topic}</div>
                  <div className="mt-1 text-xs font-extrabold uppercase tracking-widest">
                    {b.status === "canceled"
                      ? <span className="text-[#707880] inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {c.canceled}</span>
                      : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> confirmed</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {b.status === "confirmed" && b.meet_url && (
                    <a href={b.meet_url} target="_blank" rel="noopener noreferrer" data-testid={`booking-join-${b.id}`}
                      className="pod-focus text-xs font-extrabold uppercase tracking-widest bg-[#2C4A3E] text-white px-3 py-2 rounded-lg inline-flex items-center gap-1">
                      <Video className="w-3.5 h-3.5" /> {c.join}
                    </a>
                  )}
                  {b.status === "confirmed" && (
                    <button
                      onClick={() => doCancel(b.id)}
                      disabled={cancelId === b.id}
                      data-testid={`booking-cancel-${b.id}`}
                      className="pod-focus text-xs font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-3 py-2 rounded-lg disabled:opacity-60"
                    >{cancelId === b.id ? c.canceling : c.cancel}</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {user?.role === "admin" && (
        <section className="mt-10 rounded-2xl bg-[#FBF9F5] border-2 border-dashed border-[#C85A32] p-5" data-testid="sessions-admin">
          <h2 className="text-lg font-extrabold text-[#1C2024]">{c.admin}</h2>
          <p className="text-sm text-[#4A525A] mt-1">{c.admin_note}</p>

          {/* Google Calendar per-session Meet connector */}
          <div className="mt-4 rounded-2xl border-2 border-[#E4DDD3] bg-white p-4" data-testid="admin-calendar-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FAF5EC] text-[#C85A32] flex items-center justify-center shrink-0">
                <Video className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-extrabold text-[#1C2024]">{c.cal_heading}</div>
                <p className="text-xs text-[#4A525A] mt-1">{c.cal_desc}</p>
                {calStatus && (
                  <div className="mt-2 text-xs" data-testid="admin-calendar-status">
                    {!calStatus.configured ? (
                      <span className="inline-flex items-center gap-1.5 text-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5" /> {c.cal_not_configured}
                      </span>
                    ) : calStatus.connected ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {c.cal_connected(calStatus.email || "")}
                      </span>
                    ) : calStatus.error ? (
                      <span className="inline-flex items-center gap-1.5 text-red-700">
                        <AlertTriangle className="w-3.5 h-3.5" /> {c.cal_error}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[#707880]">{c.cal_disconnected}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                {calStatus?.configured && !calStatus?.connected && (
                  <button
                    onClick={connectCalendar}
                    disabled={calBusy}
                    data-testid="admin-calendar-connect"
                    className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-60 text-white text-xs font-extrabold uppercase tracking-widest py-2 px-3 rounded-xl inline-flex items-center gap-1.5"
                  >
                    {calBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
                    {c.cal_connect}
                  </button>
                )}
                {calStatus?.connected && (
                  <>
                    <button
                      onClick={connectCalendar}
                      disabled={calBusy}
                      data-testid="admin-calendar-reconnect"
                      className="pod-focus text-xs font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-3 py-2 rounded-xl disabled:opacity-60"
                    >{c.cal_reconnect}</button>
                    <button
                      onClick={disconnectCalendar}
                      disabled={calBusy}
                      data-testid="admin-calendar-disconnect"
                      className="pod-focus text-xs font-bold border-2 border-red-200 hover:border-red-400 text-red-700 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-xl disabled:opacity-60"
                    >{c.cal_disconnect}</button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.admin_from}</label>
              <input type="datetime-local" value={aFrom} onChange={(e) => setAFrom(e.target.value)} data-testid="admin-slot-from"
                className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.admin_to}</label>
              <input type="datetime-local" value={aTo} onChange={(e) => setATo(e.target.value)} data-testid="admin-slot-to"
                className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.admin_note_label}</label>
              <input value={aNote} onChange={(e) => setANote(e.target.value)} data-testid="admin-slot-note" maxLength={200}
                className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-3 py-2 text-sm"/>
            </div>
          </div>
          <button onClick={doPublish} disabled={aBusy || !aFrom || !aTo} data-testid="admin-slot-add"
            className="pod-focus mt-3 bg-[#2C4A3E] hover:bg-[#1F382E] disabled:opacity-70 text-white font-bold py-2 px-4 rounded-xl inline-flex items-center gap-2 text-sm">
            {aBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {c.admin_add}
          </button>
          <button onClick={doSeed} disabled={aBusy} data-testid="admin-slot-seed"
            className="pod-focus mt-3 ml-2 bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-2 px-4 rounded-xl inline-flex items-center gap-2 text-sm">
            {aBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} {c.admin_seed}
          </button>

          <div className="mt-6 pt-5 border-t-2 border-dashed border-[#E4DDD3]" data-testid="admin-slots-manage">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1C2024]">{c.admin_manage}</h3>
            {allSlots.length === 0 ? (
              <div className="mt-2 text-sm text-[#707880]" data-testid="admin-slots-manage-empty">{c.admin_manage_empty}</div>
            ) : (
              <ul className="mt-2 divide-y divide-[#E4DDD3]">
                {allSlots.map((s) => (
                  <li key={s.id} data-testid={`admin-slot-row-${s.id}`}
                    className="py-2 flex items-center justify-between gap-3">
                    <div className="text-sm">
                      <div className="font-bold text-[#1C2024]">{fmt(s.starts_at, lang)}</div>
                      {s.note && <div className="text-xs text-[#707880]">{s.note}</div>}
                    </div>
                    {s.is_booked ? (
                      <span title={c.admin_locked} data-testid={`admin-slot-locked-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-widest text-[#707880] bg-[#FAF5EC] border-2 border-[#E4DDD3] px-2 py-1 rounded-lg">
                        <ShieldCheck className="w-3.5 h-3.5" /> {c.admin_locked}
                      </span>
                    ) : (
                      <button
                        onClick={() => doUnpublish(s.id)}
                        disabled={unpubId === s.id}
                        data-testid={`admin-slot-unpublish-${s.id}`}
                        title={c.admin_unpublish}
                        className="pod-focus inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-widest bg-red-50 hover:bg-red-100 text-red-700 border-2 border-red-200 hover:border-red-400 px-2 py-1 rounded-lg disabled:opacity-60"
                      >
                        {unpubId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        {c.admin_unpublish}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default function Sessions() {
  const { user } = useAuth();
  const tier = user?.tier || "free";
  if (!user || !hasTier(tier, "court_ready_plus")) {
    return <PaywallGate feature="sessions" minimum="court_ready_plus"><SessionsApp /></PaywallGate>;
  }
  return <SessionsApp />;
}
